import { Grid, Typography } from '@mui/material';
import * as api from "api/Knowledge";
import { GlobalClassKeys } from 'assets/jss/common-styles';
import { AppMessageBox } from 'components/AppMessageBox';
import BoxDrawer from 'components/BoxDrawer';
import { SpinnerIndicator } from 'components/CallServer';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import { TextPluralization } from 'components/TextPluralization';
import { TopicStatisticsDto } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: TopicStatisticsDto;
    loading: boolean;
}

interface IProps {
    topicId: string;
    onClose: () => void;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class TopicStatisticsDrawer extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            loading: false,
        };
    }

    ///
    private loadData(offset: number) {

        this.setState({ loading: true, data: null });

        api.getTopicStatistics(this.props.topicId)
            .then((result) => {
                this.setState({ loading: false, data: result });
            })
            .catch((error) => {
                this.setState({ loading: false });
            });
    }

    ///
    private renderStats() {
        const { data } = this.state;
        return (
            <MuiColor variant="muted">
                <MuiContainer margin="dense">
                    <table>
                        <thead>
                            <tr>
                                <td colSpan={2}>
                                    <Typography variant="button"><b>{this.props.t("Views.Topic.Dialogs.TopicStatisticsDrawer.PeopleTitle")}</b></Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td align="right"><span>{data.followers}</span></td>
                                <td><span><TextPluralization count={data.followers} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Following" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.authors}</span></td>
                                <td><span><TextPluralization count={data.authors} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Author" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.knowledgers}</span></td>
                                <td><span><TextPluralization count={data.knowledgers} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Knowledge" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.specialists}</span></td>
                                <td><span><TextPluralization count={data.specialists} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Specialist" /></span></td>
                            </tr>
                        </tbody>
                    </table>
                </MuiContainer>

                <MuiContainer margin="dense">
                    <table>
                        <thead>
                            <tr>
                                <td colSpan={2}>
                                    <Typography variant="button"><b>{this.props.t("Views.Topic.Dialogs.TopicStatisticsDrawer.PublicationsTitle")}</b></Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td align="right"><span>{data.publicationsActive}</span></td>
                                <td><span><TextPluralization count={data.authors} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.PublicationActive" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.publicationsInstitutional}</span></td>
                                <td><span><TextPluralization count={data.publicationsInstitutional} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.PublicationInstitutional" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.highlights}</span></td>
                                <td><span><TextPluralization count={data.highlights} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Highlights" /></span></td>
                            </tr>
                        </tbody>
                    </table>
                </MuiContainer>

                <MuiContainer margin="dense">
                    <table>
                        <thead>
                            <tr>
                                <td colSpan={2}>
                                    <Typography variant="button"><b>{this.props.t("Views.Topic.Dialogs.TopicStatisticsDrawer.PublicationTypeTitle")}</b></Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td align="right"><span>{data.questions}</span></td>
                                <td><span><TextPluralization count={data.questions} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Question" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.posts}</span></td>
                                <td><span><TextPluralization count={data.posts} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Post" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.lists}</span></td>
                                <td><span><TextPluralization count={data.lists} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.List" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.polls}</span></td>
                                <td><span><TextPluralization count={data.polls} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Poll" /></span></td>
                            </tr>
                        </tbody>
                    </table>
                </MuiContainer>

                <MuiContainer margin="dense">
                    <table>
                        <thead>
                            <tr>
                                <td colSpan={2}>
                                    <Typography variant="button"><b>{this.props.t("Views.Topic.Dialogs.TopicStatisticsDrawer.QuestionsTitle")}</b></Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td align="right"><span>{data.answeredQuestions}</span></td>
                                <td><span><TextPluralization count={data.answeredQuestions} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.AnsweredQuestion" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.notAnsweredQuestions}</span></td>
                                <td><span><TextPluralization count={data.notAnsweredQuestions} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.NotAnsweredQuestion" /></span></td>
                            </tr>
                        </tbody>
                    </table>
                </MuiContainer>

                <MuiContainer margin="dense">
                    <table>
                        <thead>
                            <tr>
                                <td colSpan={2}>
                                    <Typography variant="button"><b>{this.props.t("Views.Topic.Dialogs.TopicStatisticsDrawer.ActivityTitle")}</b></Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td align="right"><span>{data.views}</span></td>
                                <td><span><TextPluralization count={data.views} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.View" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.likes}</span></td>
                                <td><span><TextPluralization count={data.likes} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Like" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.comments}</span></td>
                                <td><span><TextPluralization count={data.comments} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Comment" /></span></td>
                            </tr>
                            <tr>
                                <td align="right"><span>{data.thanks}</span></td>
                                <td><span><TextPluralization count={data.thanks} resource="Views.Topic.Dialogs.TopicStatisticsDrawer.Thank" /></span></td>
                            </tr>
                        </tbody>
                    </table>
                </MuiContainer>

            </MuiColor>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {
        const { onClose } = this.props;
        const { data, loading } = this.state;

        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={onClose}
                title={this.props.t("Views.Topic.Dialogs.TopicStatisticsDrawer.BoxTitle")}
            >
                <MuiPaper dense fullHeight elevation={0}>
                    <Grid container direction="column" wrap="nowrap" style={{ height: "100%" }}>

                        <Grid item className={GlobalClassKeys.grow}>

                            {
                                loading
                                    ? <SpinnerIndicator />
                                    : data == null
                                        ? <AppMessageBox resource="LoadListDataFailed" />
                                        : this.renderStats()
                            }
                        </Grid>

                    </Grid>
                </MuiPaper>

            </BoxDrawer>
        );
    }
}

export default withTranslation()(TopicStatisticsDrawer);